import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Photo as PhotoIcon,
  Clear as ClearIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { Tree } from 'primereact/tree';

const FolderManagementPanel = ({
  value,
  nodes,
  expandedKeys,
  setExpandedKeys,
  selectedKeys,
  handleSelect,
  selectedPaths,
  uploadedFile,
  handleFileChange,
  handleRemoveFile,
  handleFileUpload,
  path,
  setRefetchTrigger,
  convertToPrimeNodes,
}) => {
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteSvgDialogOpen, setDeleteSvgDialogOpen] = useState(false);
  
  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderName, setRenameFolderName] = useState('');
  const [selectedSvgFile, setSelectedSvgFile] = useState(null);
  const [svgToDelete, setSvgToDelete] = useState('');
  
  // File input ref
  const fileInputRef = useRef(null);
  
  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const currentPath = selectedPaths.length > 0 ? selectedPaths[selectedPaths.length - 1] : '';

  // Show notification
  const showNotification = (message, severity = 'success') => {
    console.log('Notification triggered:', message, severity);
    setNotification({ open: true, message, severity });
    
    setTimeout(() => {
      setNotification({ open: false, message: '', severity: 'success' });
    }, 3000);
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ open: false, message: '', severity: 'success' });
  };

  // ============================================
  // SVG UPLOAD HANDLERS
  // ============================================

  // SVG dosya seçme handler'ı
  const handleSvgFileSelect = (event) => {
    const file = event.target.files[0];
    
    if (!file) return;

    // Dosya tipini kontrol et
    if (!file.name.toLowerCase().endsWith('.svg')) {
      showNotification('Lütfen sadece SVG dosyası seçin', 'error');
      return;
    }

    // Dosya boyutunu kontrol et (örn: max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Dosya boyutu 5MB\'dan küçük olmalıdır', 'error');
      return;
    }

    setSelectedSvgFile(file);
    console.log('SVG file selected:', file.name);
  };

  // SVG dosyasını temizle
  const handleClearSvgFile = () => {
    setSelectedSvgFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // SVG yükleme handler'ı
  const handleUploadSvg = async () => {
    if (!selectedSvgFile) {
      showNotification('Lütfen bir SVG dosyası seçin', 'warning');
      return;
    }

    if (!currentPath) {
      showNotification('Lütfen bir klasör seçin', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedSvgFile);
    formData.append('folderPath', currentPath);

    try {
      const response = await fetch(`${path}/api/UploadImage`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        
        // Dialog'u kapat ve temizle
        handleClearSvgFile();
        setUploadDialogOpen(false);
        
        // Bildirimi göster
        setTimeout(() => {
          showNotification(data.message || 'Dosya başarıyla yüklendi', 'success');
        }, 100);
        
        // Tree'yi yenile
        setTimeout(() => {
          setRefetchTrigger((prev) => prev + 1);
        }, 200);
      } else {
        const errorText = await response.text();
        showNotification(errorText || 'Dosya yüklenemedi', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showNotification('Dosya yüklenirken hata oluştu', 'error');
    }
  };

  // ============================================
  // SVG DELETE HANDLERS
  // ============================================

  // SVG silme dialog'unu aç
  const handleOpenDeleteSvg = () => {
    if (!currentPath) {
      showNotification('Lütfen bir dosya seçin', 'warning');
      return;
    }

    // Seçili path'in SVG dosyası olup olmadığını kontrol et
    if (!currentPath.toLowerCase().endsWith('.svg') && 
        !currentPath.toLowerCase().endsWith('.png') &&
        !currentPath.toLowerCase().endsWith('.jpg') &&
        !currentPath.toLowerCase().endsWith('.jpeg')) {
      showNotification('Lütfen bir resim dosyası seçin', 'warning');
      return;
    }

    setSvgToDelete(currentPath);
    setDeleteSvgDialogOpen(true);
  };

  // SVG silme handler'ı
  const handleDeleteSvg = async () => {
    if (!svgToDelete) {
      showNotification('Silinecek dosya bulunamadı', 'error');
      return;
    }

    try {
      const response = await fetch(`${path}/api/DeleteImage`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filePath: svgToDelete }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Dialog'u kapat
        setDeleteSvgDialogOpen(false);
        setSvgToDelete('');
        
        // Bildirimi göster
        setTimeout(() => {
          showNotification(data.message || 'Dosya başarıyla silindi', 'success');
        }, 100);
        
        // Tree'yi yenile
        setTimeout(() => {
          setRefetchTrigger((prev) => prev + 1);
        }, 200);
      } else {
        const errorText = await response.text();
        showNotification(errorText || 'Dosya silinemedi', 'error');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showNotification('Dosya silinirken hata oluştu', 'error');
    }
  };

  // ============================================
  // FOLDER MANAGEMENT HANDLERS (Mevcut)
  // ============================================

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showNotification('Lütfen klasör adı girin', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('folderName', newFolderName);
    formData.append('folderPath', currentPath || '');

    try {
      const response = await fetch(`${path}/api/CreateFolder`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewFolderName('');
        setCreateDialogOpen(false);
        
        setTimeout(() => {
          showNotification(data.message || 'Klasör başarıyla oluşturuldu', 'success');
        }, 100);
        
        setTimeout(() => {
          setRefetchTrigger((prev) => prev + 1);
        }, 200);
      } else {
        showNotification(data.message || 'Klasör oluşturulamadı', 'error');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      showNotification('Klasör oluşturulurken hata oluştu', 'error');
    }
  };

  const handleDeleteFolder = async () => {
    if (!currentPath) {
      showNotification('Lütfen silinecek klasörü seçin', 'warning');
      return;
    }

    try {
      const response = await fetch(`${path}/api/DeleteFolder`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folderPath: currentPath }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setDeleteDialogOpen(false);
        
        setTimeout(() => {
          showNotification(data.message || 'Klasör başarıyla silindi', 'success');
        }, 100);
        
        setTimeout(() => {
          setRefetchTrigger((prev) => prev + 1);
        }, 200);
      } else {
        showNotification(data.message || 'Klasör silinemedi', 'error');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      showNotification('Klasör silinirken hata oluştu', 'error');
    }
  };

  const handleRenameFolder = async () => {
    if (!renameFolderName.trim()) {
      showNotification('Lütfen yeni klasör adı girin', 'warning');
      return;
    }

    if (!currentPath) {
      showNotification('Lütfen yeniden adlandırılacak klasörü seçin', 'warning');
      return;
    }

    try {
      const response = await fetch(`${path}/api/RenameFolder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oldPath: currentPath,
          newName: renameFolderName,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRenameFolderName('');
        setRenameDialogOpen(false);
        
        setTimeout(() => {
          showNotification(data.message || 'Klasör başarıyla yeniden adlandırıldı', 'success');
        }, 100);
        
        setTimeout(() => {
          setRefetchTrigger((prev) => prev + 1);
        }, 200);
      } else {
        showNotification(data.message || 'Klasör yeniden adlandırılamadı', 'error');
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      showNotification('Klasör yeniden adlandırılırken hata oluştu', 'error');
    }
  };

  return (
    <>
      {/* Notification Banner */}
      {notification.open && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '400px',
            maxWidth: '600px',
            backgroundColor: 
              notification.severity === 'success' ? '#4caf50' :
              notification.severity === 'error' ? '#f44336' :
              notification.severity === 'warning' ? '#ff9800' : '#2196f3',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            borderRadius: '0 0 8px 8px',
            animation: 'slideDown 0.3s ease-out',
            '@keyframes slideDown': {
              from: {
                transform: 'translateX(-50%) translateY(-100%)',
                opacity: 0,
              },
              to: {
                transform: 'translateX(-50%) translateY(0)',
                opacity: 1,
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <Box sx={{ fontSize: '28px' }}>
              {notification.severity === 'success' && '✓'}
              {notification.severity === 'error' && '✕'}
              {notification.severity === 'warning' && '⚠'}
              {notification.severity === 'info' && 'ℹ'}
            </Box>
            <Box sx={{ flex: 1, fontSize: '16px', fontWeight: 500 }}>
              {notification.message}
            </Box>
            <IconButton 
              size="small" 
              onClick={handleCloseNotification}
              sx={{ color: 'white' }}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box sx={{ p: 2 }}>
        {/* Tree View */}
        <Box sx={{ height: '60%', width: '95%', mb: 3 }}>
          <Tree
            value={convertToPrimeNodes(nodes)}
            expandedKeys={expandedKeys}
            onToggle={(e) => setExpandedKeys(e.value)}
            selectionMode="checkbox"
            selectionKeys={selectedKeys}
            onSelectionChange={handleSelect}
            className="w-full md:w-30rem"
            filter
            filterMode="lenient"
            propagateSelectionDown={false}
            style={{
              maxHeight: '400px',
              maxWidth: '100%',
              backgroundColor: '#9fb0b0',
              overflow: 'auto',
            }}
          />
        </Box>

        {/* Selected Path Display */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FolderOpenIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Box>
            <strong>Seçili Konum:</strong>
            <Box
              component="span"
              sx={{
                ml: 2,
                color: currentPath ? '#1976d2' : '#666',
                fontFamily: 'monospace',
              }}
            >
              {currentPath || 'Ana Dizin'}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ============================================ */}
        {/* FOLDER MANAGEMENT SECTION */}
        {/* ============================================ */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, color: '#1976d2' }}>
            📁 Klasör Yönetimi
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CreateNewFolderIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ minWidth: '180px' }}
            >
              Yeni Klasör
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<EditIcon />}
              onClick={() => {
                setRenameFolderName('');
                setRenameDialogOpen(true);
              }}
              disabled={!currentPath}
              sx={{ minWidth: '180px' }}
            >
              Yeniden Adlandır
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={!currentPath}
              sx={{ minWidth: '180px' }}
            >
              Klasör Sil
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ============================================ */}
        {/* SVG/TEMPLATE MANAGEMENT SECTION */}
        {/* ============================================ */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, color: '#2e7d32' }}>
            🖼️ Dizayn Template Yönetimi
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={!currentPath}
              sx={{ minWidth: '180px' }}
            >
              SVG Yükle
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteSvg}
              disabled={!currentPath}
              sx={{ minWidth: '180px' }}
            >
              Dosya Sil
            </Button>
          </Box>
          {!currentPath && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Dizayn yüklemek veya silmek için lütfen bir klasör seçin
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ============================================ */}
        {/* DIALOGS */}
        {/* ============================================ */}

        {/* Create Folder Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Yeni Klasör Oluştur</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Konum: <strong>{currentPath || 'Ana Dizin'}</strong>
              </Alert>
              <TextField
                autoFocus
                fullWidth
                label="Klasör Adı"
                variant="outlined"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>İptal</Button>
            <Button onClick={handleCreateFolder} variant="contained" color="primary">
              Oluştur
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Folder Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Klasörü Sil</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              <strong>{currentPath}</strong> klasörünü silmek istediğinizden emin misiniz?
              <br />
              <strong>Bu işlem geri alınamaz!</strong>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button onClick={handleDeleteFolder} variant="contained" color="error">
              Sil
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rename Folder Dialog */}
        <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Klasörü Yeniden Adlandır</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Mevcut: <strong>{currentPath}</strong>
              </Alert>
              <TextField
                autoFocus
                fullWidth
                label="Yeni Klasör Adı"
                variant="outlined"
                value={renameFolderName}
                onChange={(e) => setRenameFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameFolder();
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenameDialogOpen(false)}>İptal</Button>
            <Button onClick={handleRenameFolder} variant="contained" color="warning">
              Yeniden Adlandır
            </Button>
          </DialogActions>
        </Dialog>

        {/* SVG Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => {
          setUploadDialogOpen(false);
          handleClearSvgFile();
        }} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon color="success" />
              SVG Template Yükle
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Hedef Klasör: <strong>{currentPath}</strong>
              </Alert>

              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".svg"
                style={{ display: 'none' }}
                onChange={handleSvgFileSelect}
              />

              {/* Upload Area */}
              <Box
                onClick={() => !selectedSvgFile && fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed #2e7d32',
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                  cursor: selectedSvgFile ? 'default' : 'pointer',
                  backgroundColor: selectedSvgFile ? '#e8f5e9' : '#f5f5f5',
                  transition: 'all 0.3s',
                  '&:hover': {
                    backgroundColor: selectedSvgFile ? '#e8f5e9' : '#eeeeee',
                  },
                }}
              >
                {selectedSvgFile ? (
                  <Box>
                    <InsertDriveFileIcon sx={{ fontSize: 48, color: '#2e7d32', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {selectedSvgFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedSvgFile.size / 1024).toFixed(2)} KB
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSvgFile();
                        }}
                      >
                        Dosyayı Kaldır
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      SVG dosyası seçmek için tıklayın
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      veya dosyayı sürükleyip bırakın
                    </Typography>
                  </Box>
                )}
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                Sadece <strong>.svg</strong> formatındaki dosyalar yüklenebilir (Max: 5MB)
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUploadDialogOpen(false);
              handleClearSvgFile();
            }}>
              İptal
            </Button>
            <Button 
              onClick={handleUploadSvg} 
              variant="contained" 
              color="success"
              disabled={!selectedSvgFile}
              startIcon={<CloudUploadIcon />}
            >
              Yükle
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete SVG Dialog */}
        <Dialog open={deleteSvgDialogOpen} onClose={() => setDeleteSvgDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              Dosyayı Sil
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error">
              <strong>{svgToDelete.split('/').pop()}</strong> dosyasını silmek istediğinizden emin misiniz?
              <br /><br />
              Tam Yol: <code>{svgToDelete}</code>
              <br /><br />
              <strong>Bu işlem geri alınamaz!</strong>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteSvgDialogOpen(false)}>İptal</Button>
            <Button onClick={handleDeleteSvg} variant="contained" color="error" startIcon={<DeleteIcon />}>
              Sil
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default FolderManagementPanel;